import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Collapse, Flex, Skeleton, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  DocumentReviewStatus,
  DocumentReviewType,
  EvidenceProgress,
  EvidenceSectionNameMap,
  EvidenceSectionName,
  EvidenceTask,
  EvidenceTaskNameMap,
  EvidenceTaskType,
  EvidenceTaskUploadFiles,
  EvidenceTaskStatus,
} from 'api/brokerCases/types';
import { useQueries } from '@tanstack/react-query';
import { brokerCasesApi } from 'api/brokerCases/brokerCasesApi';
import { DOCUMENT_REVIEWS_QUERY_KEY } from 'queries/brokerCases/useDocumentReviews/useDocumentReviews';
import { DOCUMENT_REVIEW_TYPE_TO_RELEVANT_TASKS } from 'pages/EvidencePage/evidenceConfig';
import { getEvidenceSectionDescription, getEvidenceSectionLabel } from 'pages/EvidencePage/evidenceConfig';
import { EvidenceTypeIcon } from './EvidenceTypeIcon';
import { EvidenceCompletedBadge } from './EvidenceCompletedBadge';
import { EvidenceSectionCompletedIcon } from './EvidenceSectionCompletedIcon';
import { EvidenceItemProgress } from './EvidenceItemProgess';
import { ApplicantOption, getCountOfPendingTasksPerApplicant, getIndividualTasks } from 'pages/EvidencePage/helpers';
import { EvidenceApplicantSwitcher } from './EvidenceApplicantSwitcher';
import { EvidenceItemActionList } from './EvidenceItemActionList';
import { EvidenceUploadedFilesList } from './EvidenceItemActionListItem/components/EvidenceUploadedFilesList';
import { TypedFormatMessageFn } from 'types/intl';

export interface IEvidenceListItemProps {
  name: EvidenceSectionName;
  applicants: ApplicantOption[];
  tasks: EvidenceTask[];
  progress: EvidenceProgress;
  isCaseTasksLoading: boolean;
}

const INDIVIDUAL_TASK_PREFIX = 'case/individual';

export const EvidenceListItem = ({ name, applicants, tasks, progress, isCaseTasksLoading }: IEvidenceListItemProps) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const [activeIndividualId, setActiveIndividualId] = useState<string | null>(applicants?.[0]?.id ?? null);
  const [isProgressLoading, setIsProgressLoading] = useState(false);
  const [activeKey, setActiveKey] = useState<string | string[]>([]);
  const prevCompletedRef = useRef(progress.completedCount === progress.totalCount);
  const isOtherDocumentsSection = name === EvidenceSectionNameMap.OTHER_DOCUMENTS;
  const isFurtherInformationRequired = name === EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED;

  const showAllUploadedFilesForSection = useMemo(
    () =>
      name === EvidenceSectionNameMap.PROOF_OF_IDENTITY ||
      name === EvidenceSectionNameMap.PROOF_OF_DEPOSIT ||
      name === EvidenceSectionNameMap.OTHER_DOCUMENTS ||
      name === EvidenceSectionNameMap.FURTHER_INFORMATION_REQUIRED,
    [name],
  );

  const hasIndividualTasks = useMemo(() => tasks.some(task => task.name.startsWith(INDIVIDUAL_TASK_PREFIX)), [tasks]);

  const isSectionCompleted = useMemo(() => {
    return progress.completedCount === progress.totalCount;
  }, [progress]);

  const reviewRelevantTasks = useMemo(
    () =>
      tasks.filter(task =>
        DOCUMENT_REVIEW_TYPE_TO_RELEVANT_TASKS[DocumentReviewType.CUSTOMER_FINANCES].includes(task.name),
      ),
    [tasks],
  );

  const uniqueIndividualIds = useMemo(() => {
    const ids = new Set<string>();
    for (const task of reviewRelevantTasks) {
      if (task.payload.individualId) {
        ids.add(task.payload.individualId);
      }
    }
    return Array.from(ids);
  }, [reviewRelevantTasks]);

  const caseId = tasks[0]?.payload.caseId ?? '';

  const completedReviewQueries = useQueries({
    queries: uniqueIndividualIds.map(individualId => ({
      queryKey: [DOCUMENT_REVIEWS_QUERY_KEY, caseId, individualId, DocumentReviewStatus.COMPLETED],
      queryFn: () =>
        brokerCasesApi.getDocumentReviews(
          caseId,
          individualId,
          DocumentReviewStatus.COMPLETED,
          DocumentReviewType.CUSTOMER_FINANCES,
        ),
      refetchInterval: 5000,
      enabled: !!caseId && reviewRelevantTasks.length > 0,
    })),
  });

  const ongoingReviewQueries = useQueries({
    queries: uniqueIndividualIds.map(individualId => ({
      queryKey: [DOCUMENT_REVIEWS_QUERY_KEY, caseId, individualId, DocumentReviewStatus.ONGOING],
      queryFn: () =>
        brokerCasesApi.getDocumentReviews(
          caseId,
          individualId,
          DocumentReviewStatus.ONGOING,
          DocumentReviewType.CUSTOMER_FINANCES,
        ),
      refetchInterval: 5000,
      enabled: !!caseId && reviewRelevantTasks.length > 0,
    })),
  });

  const hasFailedReview = useMemo(() => {
    return completedReviewQueries.some(query => {
      const reviews = query.data;
      if (!reviews || reviews.length === 0) return false;
      return reviews.some(
        review =>
          review.issues[EvidenceTaskNameMap.LAST_3_MONTHS_BANK_STATEMENTS].length > 0 ||
          review.issues[EvidenceTaskNameMap.LATEST_3_MONTHS_PAYSLIPS].length > 0,
      );
    });
  }, [completedReviewQueries]);

  const isUnderReview = useMemo(() => {
    return ongoingReviewQueries.some(query => {
      const reviews = query.data;
      return reviews && reviews.length > 0;
    });
  }, [ongoingReviewQueries]);

  const useFurtherInfoAlertStyle = isFurtherInformationRequired && !isSectionCompleted;

  useEffect(() => {
    if (isSectionCompleted && !prevCompletedRef.current && !isOtherDocumentsSection) {
      setActiveKey([]);
    }
    prevCompletedRef.current = isSectionCompleted;
  }, [isSectionCompleted, isOtherDocumentsSection]);

  const selectedIndividualId = useMemo(() => {
    if (!applicants.length) {
      return null;
    }
    if (activeIndividualId && applicants.some(a => a.id === activeIndividualId)) {
      return activeIndividualId;
    }
    return applicants[0].id;
  }, [applicants, activeIndividualId]);

  useEffect(() => {
    if (!hasIndividualTasks || !applicants.length) {
      return;
    }
    const isValid = activeIndividualId != null && applicants.some(a => a.id === activeIndividualId);
    if (!isValid) {
      setActiveIndividualId(applicants[0].id);
    }
  }, [applicants, activeIndividualId, hasIndividualTasks]);

  const tasksForSelectedApplicant = useMemo(() => {
    if (!hasIndividualTasks) {
      return tasks;
    }
    const caseLevelTasks = tasks.filter(t => t.payload.individualId == null);

    if (!applicants.length || !selectedIndividualId) {
      return caseLevelTasks;
    }

    return [...caseLevelTasks, ...getIndividualTasks(tasks, selectedIndividualId)];
  }, [tasks, applicants.length, hasIndividualTasks, selectedIndividualId]);

  let sectionFiles = useMemo(() => {
    const raw = tasksForSelectedApplicant
      .filter((t): t is EvidenceTaskUploadFiles => t.payload.taskType === EvidenceTaskType.UPLOAD_FILE)
      .flatMap(t => t.payload.files);
    const seen = new Set<number>();
    return raw.filter(f => {
      if (seen.has(f.id)) {
        return false;
      }
      seen.add(f.id);
      return true;
    });
  }, [tasksForSelectedApplicant]);

  const mostRecentCompletedTask = useMemo(() => {
    return tasksForSelectedApplicant
      .filter(t => t.status === EvidenceTaskStatus.COMPLETED)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  }, [tasksForSelectedApplicant]);

  const mostRecentCompletedTaskDocIds = useMemo(() => {
    if (mostRecentCompletedTask && 'metadata' in mostRecentCompletedTask) {
      return mostRecentCompletedTask.metadata?.docIds ?? [];
    }
    return [];
  }, [mostRecentCompletedTask]);

  // filters down the files shown in the Uploaded List to only the ones done for the most recent completed task
  sectionFiles = useMemo(() => {
    if (showAllUploadedFilesForSection || mostRecentCompletedTaskDocIds.length === 0) {
      return sectionFiles;
    }
    return sectionFiles.filter(f => mostRecentCompletedTaskDocIds.includes(f.id));
  }, [sectionFiles, mostRecentCompletedTaskDocIds, showAllUploadedFilesForSection]);

  return (
    <Collapse
      activeKey={activeKey}
      onChange={setActiveKey}
      className={`[&_.ant-collapse-header]:!px-0 [&_.ant-collapse-header]:!pt-0 ${
        useFurtherInfoAlertStyle ? '[&_.ant-collapse-header]:!pb-4' : '[&_.ant-collapse-header]:!pb-0'
      } ${
        useFurtherInfoAlertStyle ? 'px-6 pt-4 pb-0' : 'px-6 py-4'
      } [&_.ant-collapse-item]:!border-0 [&_.ant-collapse-header]:!flex [&_.ant-collapse-header-text]:!min-w-0 [&_.ant-collapse-header-text]:!flex-1 [&_.ant-collapse-expand-icon]:!m-0 [&_.ant-collapse-expand-icon]:!flex [&_.ant-collapse-expand-icon]:!h-auto [&_.ant-collapse-expand-icon]:!min-h-0 [&_.ant-collapse-expand-icon]:!items-center [&_.ant-collapse-expand-icon]:!justify-center [&_.ant-collapse-expand-icon]:shrink-0 [&_.ant-collapse-expand-icon]:!self-center [&_.ant-collapse-expand-icon]:!text-[#000] [&_.ant-collapse-content]:-mx-6 [&_.ant-collapse-content]:border-t ${
        useFurtherInfoAlertStyle
          ? '[&_.ant-collapse-content]:mt-0 [&_.ant-collapse-content]:border-[#FFD6A8] [&_.ant-collapse-content]:bg-white'
          : '[&_.ant-collapse-content]:mt-4 [&_.ant-collapse-content]:border-[#f0f0f0]'
      } [&_.ant-collapse-content-box]:!p-0 ${
        useFurtherInfoAlertStyle ? '!border !border-[#FFD6A8] bg-[#FFF7ED]' : '!border-0 bg-white'
      }`}
      expandIcon={({ isActive }) => (isActive ? <UpOutlined /> : <DownOutlined />)}
      expandIconPosition='end'
      items={[
        {
          key: name,
          styles: {
            header: {
              alignItems: 'center',
            },
          },
          label: (
            <Flex align='center' className='w-full min-w-0 gap-5'>
              {(isSectionCompleted || hasFailedReview || isUnderReview) && !isOtherDocumentsSection ? (
                <EvidenceSectionCompletedIcon
                  hasFailedReview={hasFailedReview && !isUnderReview}
                  isUnderReview={isUnderReview}
                  taskCompleted={isSectionCompleted}
                />
              ) : (
                <EvidenceTypeIcon name={name} />
              )}
              <Flex vertical className='min-w-0 flex-1 gap-1'>
                <Typography.Text className='text-sm font-bold leading-5'>
                  {getEvidenceSectionLabel(name, formatMessage)}
                </Typography.Text>
                <Typography.Text className='text-sm font-normal leading-5 text-gray-500'>
                  {getEvidenceSectionDescription(name, formatMessage)}
                </Typography.Text>
              </Flex>
              {!isOtherDocumentsSection ? (
                isSectionCompleted ? (
                  <Flex className='ml-auto'>
                    <EvidenceCompletedBadge />
                  </Flex>
                ) : isCaseTasksLoading || isProgressLoading ? (
                  <Skeleton.Input active size='small' className='ml-auto' />
                ) : (
                  <EvidenceItemProgress progress={progress} />
                )
              ) : null}
            </Flex>
          ),
          children: (
            <Flex vertical align='start' className={useFurtherInfoAlertStyle ? 'p-6 gap-2' : 'px-6 pt-6 pb-2 gap-2'}>
              {hasIndividualTasks && applicants.length > 1 && selectedIndividualId && (
                <EvidenceApplicantSwitcher
                  applicants={applicants.map(applicant => ({
                    ...applicant,
                    pendingCount: getCountOfPendingTasksPerApplicant(tasks, applicant.id),
                  }))}
                  activeIndividualId={selectedIndividualId}
                  onSelect={setActiveIndividualId}
                />
              )}
              <EvidenceItemActionList
                sectionName={name}
                tasks={tasksForSelectedApplicant}
                applicants={applicants}
                onProgressLoadingChange={setIsProgressLoading}
                listItemKeyPrefix={hasIndividualTasks && selectedIndividualId ? selectedIndividualId : undefined}
              />
              {sectionFiles.length > 0 && <EvidenceUploadedFilesList files={sectionFiles} />}
            </Flex>
          ),
        },
      ]}
    />
  );
};
